import React, { useState, useEffect } from "react";
import TutorLayout from "./TutorLayout";
import { getClipsFromFirebase, getFirebaseQuestions } from "./firebaseQuestions";
import { getDocs, collection, addDoc } from "firebase/firestore";
import { db } from "./firebase";
import Latex from "react-latex-next";
import "katex/dist/katex.min.css";
import "./AssignmentBuilder.css";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


function AssignmentBuilder() {
  // Utility to render text mixed with LaTeX
const renderMixedLatex = (text) => {
  if (!text) return null;

  // Split text into parts that look like math vs plain text
  // (this assumes $$...$$ is block math, $...$ is inline math)
  const parts = text.split(/(\${1,2}[^$]+\${1,2})/g);

  return parts.map((part, i) => {
    if (part.startsWith("$$") && part.endsWith("$$")) {
      return <BlockMath key={i}>{part.slice(2, -2)}</BlockMath>;
    }
    if (part.startsWith("$") && part.endsWith("$")) {
      return <InlineMath key={i}>{part.slice(1, -1)}</InlineMath>;
    }
    return <span key={i}>{part}</span>;
  });
};




  const [subject, setSubject] = useState("GCSE Maths");
  const [topic, setTopic] = useState("");
  const [subtopic, setSubtopic] = useState("");
  const [topicsData, setTopicsData] = useState({});
  const [version, setVersion] = useState("A");
  const [clips, setClips] = useState([]);
  const [selectedClip, setSelectedClip] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [assignmentTitle, setAssignmentTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [activityType, setActivityType] = useState("self-marked-quiz");
  const [animationStates, setAnimationStates] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    async function fetchTopics() {
      const snapshot = await getDocs(collection(db, "questions"));
      const questions = snapshot.docs.map(doc => doc.data());
      const map = {};

      questions.forEach(q => {
        if (!map[q.subject]) map[q.subject] = {};
        if (!map[q.subject][q.topic]) map[q.subject][q.topic] = new Set();
        map[q.subject][q.topic].add(q.subtopic);
      });

      const structured = {};
      for (const subj in map) {
        structured[subj] = {};
        for (const top in map[subj]) {
          structured[subj][top] = Array.from(map[subj][top]);
        }
      }
      setTopicsData(structured);
    }
    fetchTopics();
  }, []);

  useEffect(() => {
    async function fetchClips() {
      if (subject && topic && subtopic) {
        const fetchedClips = await getClipsFromFirebase(subject, topic, subtopic);
        setClips(fetchedClips);
      } else {
        setClips([]);
      }
      setSelectedClip(null);
      setQuestions([]);
    }
    fetchClips();
  }, [subject, topic, subtopic]);

  useEffect(() => {
    async function fetchQuestions() {
      if (selectedClip) {
        const fetched = await getFirebaseQuestions(
          subject,
          topic,
          subtopic,
          selectedClip.name,
          version
        );

        if (subject === "GCSE Maths") {
          const sortedQuestions = fetched.sort((a, b) => {
            const difficultyA = parseInt(a.difficulty, 10) || 0;
            const difficultyB = parseInt(b.difficulty, 10) || 0;
            return difficultyA - difficultyB;
          });
          setQuestions(sortedQuestions);
        } else {
          setQuestions(fetched);
        }
        setCurrentQuestionIndex(0);
      }
    }
    fetchQuestions();
  }, [selectedClip, version, subject]);
  
  const SortableItem = ({ q, index, handleRemove }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({ id: q.id });
  
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      backgroundColor: "#fff",
      border: "1px solid #ddd",
      borderRadius: "6px",
      padding: "1rem",
      marginBottom: "0.75rem",
      cursor: "grab",
      display: "flex",
      flexDirection: "column",
    };
  
    return (
      <div ref={setNodeRef} style={style} {...attributes}>
        {/* ✅ Question number */}
        <div style={{ fontWeight: "bold", color: "#555", marginBottom: "0.5rem" }}>
          Question {index + 1}
        </div>
  
        {/* ✅ Drag body */}
        <div {...listeners}>
        <div className="question-content">
  {q.questionParagraphs?.length > 0 ? (
    q.questionParagraphs.map((p, i) => {
      let paragraph = p;
      if (typeof p === "string") {
        try {
          const parsed = JSON.parse(p);
          if (parsed && (parsed.text || parsed.image)) {
            paragraph = parsed;
          }
        } catch {
         return (
  <p key={i}>
    <Latex>{`$${p}$`}</Latex>
  </p>
);
        }
      }

      if (typeof paragraph === "object" && (paragraph.text || paragraph.image)) {
        return (
          <div key={i} style={{ marginBottom: "1rem" }}>
            {paragraph.text && (
  <p>
    <Latex>{`$${paragraph.text}$`}</Latex>
  </p>
)}

            {paragraph.image && (
              <img
                src={paragraph.image}
                alt={`Question ${index + 1} image`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "250px",
                  objectFit: "contain",
                  display: "block",
                  margin: "0.5rem auto",
                  borderRadius: "6px"
                }}
              />
            )}
          </div>
        );
      }

      return <p key={i}>{JSON.stringify(paragraph)}</p>;
    })
  ) : (
    <>
      {q.questionText && (
  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', marginBottom: "1rem" }}>
    {renderMixedLatex(q.questionText)}
  </div>
)}
      {q.questionFormat === "text" && q.questionLatex && (
  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', marginTop: "0.5rem" }}>
    {renderMixedLatex(q.questionLatex)}
  </div>
)}

{(q.questionFormat === "latex" || !q.questionFormat) && q.questionLatex && (
  <div style={{ marginTop: "0.5rem" }}>
    <Latex>{`$$${q.questionLatex}$$`}</Latex>
  </div>
)}

      {q.optionalQuestionText && <p>{q.optionalQuestionText}</p>}
    </>
  )}
</div>

        </div>
  
        {/* Delete button */}
        <div className="question-actions">
          <button
            onClick={() => handleRemove(q.id)}
            style={{
              marginTop: "0.5rem",
              padding: "0.4rem 0.6rem",
              background: "#ffeef0",
              color: "#c0392b",
              border: "1px solid #e5bcbc",
              borderRadius: "8px",
              fontSize: "1.2rem",
              cursor: "pointer",
              zIndex: 1000,
            }}
          >
            ❌
          </button>
        </div>
      </div>
    );
  };
  
  
  
  const handleAddQuestion = (question) => {
    if (!selectedQuestions.some((q) => q.id === question.id)) {
      setSelectedQuestions(prev => [...prev, question]);
    }
  };

  const handleRemoveQuestion = (id) => {
    setSelectedQuestions(selectedQuestions.filter((q) => q.id !== id));
  };

  const moveQuestion = (index, direction) => {
    const newList = [...selectedQuestions];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newList.length) return;

    [newList[index], newList[targetIndex]] = [newList[targetIndex], newList[index]];
    setSelectedQuestions(newList);

    const newAnimations = new Array(newList.length).fill("");
    newAnimations[index] = direction === -1 ? "move-up" : "move-down";
    setAnimationStates(newAnimations);

    setTimeout(() => {
      setAnimationStates(new Array(newList.length).fill(""));
    }, 300);
  };

  const handleSaveAssignment = async () => {
    let reorderedQuestions = selectedQuestions;

    if (activityType === "blockbusters") {
      const left = selectedQuestions.filter(q => q.difficulty <= 4);
      const right = selectedQuestions.filter(q => q.difficulty >= 8);
      const middle = selectedQuestions.filter(q => q.difficulty >= 5 && q.difficulty <= 7);
    
      // ✅ Create a global pool that will be consumed uniquely
      const pool = [...selectedQuestions];
    
      // ✅ Keep track of used IDs
      const usedIds = new Set();
    
      const pick = (source, n) => {
        const result = [];
        const localSource = [...source];
    
        while (result.length < n && localSource.length > 0) {
          const index = Math.floor(Math.random() * localSource.length);
          const candidate = localSource.splice(index, 1)[0];
          if (!usedIds.has(candidate.id)) {
            result.push(candidate);
            usedIds.add(candidate.id);
          }
        }
    
        // Fallback from the global pool — without duplicates
        while (result.length < n && pool.length > 0) {
          const index = Math.floor(Math.random() * pool.length);
          const fallback = pool.splice(index, 1)[0];
          if (!usedIds.has(fallback.id)) {
            result.push(fallback);
            usedIds.add(fallback.id);
          }
        }
    
        return result;
      };
    
      const leftGroup = pick(left, 4);
      const middleGroup = pick(middle, 12);
      const rightGroup = pick(right, 4);
    
      reorderedQuestions = [...leftGroup, ...middleGroup, ...rightGroup];
    }
    if (!assignmentTitle || selectedQuestions.length === 0) {
      alert("Please provide a title and select at least one question.");
      return;
    }

    try {
      await addDoc(collection(db, "assignments"), {
        title: assignmentTitle,
        notes,
        subject,
        topic,
        subtopic,
        clip: selectedClip?.name || "",
        version,
        questions: reorderedQuestions.map(q => q.id),
        activityType,
        isDraft: true,
        createdAt: new Date().toISOString(),
      });

      alert("✅ Assignment draft saved!");
      setAssignmentTitle("");
      setNotes("");
      setSelectedQuestions([]);
    } catch (error) {
      console.error("❌ Error saving assignment:", error);
      alert("Something went wrong.");
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  const isAdded = selectedQuestions.some(q => q.id === currentQuestion?.id);

  const renderParagraphs = (paragraphs) =>
  paragraphs.map((p, i) => {
    let paragraph = p;

    // Try to parse if it's a stringified object
    if (typeof p === "string") {
      try {
        const maybeObject = JSON.parse(p);
        if (maybeObject && (maybeObject.text || maybeObject.image)) {
          paragraph = maybeObject;
        }
      } catch {
        // Not JSON, just treat as plain LaTeX string
        return (
          <p key={i}>
            <Latex>{`$${p}$`}</Latex>
          </p>
        );
      }
    }

    if (typeof paragraph === "object" && (paragraph.text || paragraph.image)) {
      return (
        <div key={i} style={{ marginBottom: "1rem" }}>
          {paragraph.text && (
            <p>
              <Latex>{`$${paragraph.text}$`}</Latex>
            </p>
          )}
          {paragraph.image && (
            <img
              src={paragraph.image}
              alt={`Image ${i + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "250px",
                objectFit: "contain",
                display: "block",
                margin: "0.5rem auto",
              }}
            />
          )}
        </div>
      );
    }

    return (
      <p key={i}>
        <Latex>{`$${JSON.stringify(paragraph)}$`}</Latex>
      </p>
    );
  });

  

  return (
    <TutorLayout>
      <div className="builder-wrapper">
        <h1>📘 Assignment Builder</h1>

        {/* --- Selectors --- */}
        <div className="section">
          <label className="dropdown-label">Subject:</label>
          <select value={subject} onChange={(e) => setSubject(e.target.value)} className="fancy-dropdown">
            {Object.keys(topicsData).map((subj) => (
              <option key={subj}>{subj}</option>
            ))}
          </select>

          <label className="dropdown-label">Main Topic:</label>
          <select value={topic} onChange={(e) => setTopic(e.target.value)} className="fancy-dropdown">
            <option value="">-- Select Topic --</option>
            {Object.keys(topicsData[subject] || {}).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          {topic && (
            <>
              <label className="dropdown-label">Subtopic:</label>
              <select value={subtopic} onChange={(e) => setSubtopic(e.target.value)} className="fancy-dropdown">
                <option value="">-- Select Subtopic --</option>
                {(topicsData[subject]?.[topic] || []).map((sub) => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>
            </>
          )}

          <label className="dropdown-label">Assignment Version:</label>
          <select value={version} onChange={(e) => setVersion(e.target.value)} className="fancy-dropdown">
            <option value="A">Set A</option>
            <option value="B">Set B</option>
          </select>
        </div>

        {/* --- Clip Selector --- */}
        {clips.length > 0 && (
          <div className="section">
            <label>Select Clip:</label>
            <div className="clip-list">
              {clips.map((clip) => (
                <button
                  key={clip.id}
                  onClick={() => setSelectedClip(clip)}
                  className={selectedClip?.id === clip.id ? "active" : ""}
                >
                  {clip.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* --- Question Display --- */}
        {currentQuestion && (
          <div className="section">
            <h3>Question {currentQuestionIndex + 1} of {questions.length}</h3>
            <div className="question-card">
              <div>
              {currentQuestion.questionParagraphs?.length > 0 ? (
  renderParagraphs(currentQuestion.questionParagraphs)
) : currentQuestion.questionHTML ? (
  <div dangerouslySetInnerHTML={{ __html: currentQuestion.questionHTML }} />
) : currentQuestion.textLines?.length > 0 ? (
  currentQuestion.textLines.map((line, i) => (
    <div key={i} style={{ marginBottom: "10px" }}>
      <Latex>{line}</Latex>
    </div>
  ))
) : currentQuestion.questionFormat === "text" && currentQuestion.questionLatex ? (
  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', marginBottom: "1rem" }}>
    {renderMixedLatex(currentQuestion.questionLatex)}
  </div>
) : currentQuestion.questionLatex ? (

  <div style={{ marginBottom: "1rem" }}>
    <Latex>{`$$${currentQuestion.questionLatex}$$`}</Latex>
  </div>
) : (
  <em>⚠️ No question content found.</em>
)}

                {currentQuestion.optionalQuestionText && (
                  <p>{currentQuestion.optionalQuestionText}</p>
                )}
              </div>
              <button
                className={`add-btn ${isAdded ? "clicked" : ""}`}
                onClick={() => !isAdded && handleAddQuestion(currentQuestion)}
                disabled={isAdded}
              >
                {isAdded ? "✅ Added" : "➕ Add to Assignment"}
              </button>
            </div>
            <div className="nav-buttons">
              <button className="nav-btn" onClick={() => setCurrentQuestionIndex(i => i - 1)} disabled={currentQuestionIndex === 0}>⬅ Previous</button>
              <button className="nav-btn" onClick={() => setCurrentQuestionIndex(i => i + 1)} disabled={currentQuestionIndex >= questions.length - 1}>Next ➡</button>
            </div>
          </div>
        )}

        {/* --- Assignment Panel --- */}
        <div className="section">
          <h3>📋 Assignment Details</h3>

          <label className="dropdown-label">Activity Type:</label>
          <select
            value={activityType}
            onChange={(e) => setActivityType(e.target.value)}
            className="fancy-dropdown"
          >
            <option value="self-marked-quiz">Self-marked Quiz</option>
            <option value="millionaire">Who Wants to Be a Millionaire</option>
            <option value="blockbusters">Blockbusters</option>
          </select>

          <input
            type="text"
            placeholder="Assignment Title"
            value={assignmentTitle}
            onChange={(e) => setAssignmentTitle(e.target.value)}
          />
          <textarea
            placeholder="Add any notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

<h4>✅ Selected Questions:</h4>

{selectedQuestions.length === 0 ? (
  <p style={{ padding: "0.5rem", color: "#777" }}>No questions added yet.</p>
) : (
  <DndContext
  sensors={sensors}
  collisionDetection={closestCenter}
  onDragEnd={({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = selectedQuestions.findIndex((q) => q.id === active.id);
    const newIndex = selectedQuestions.findIndex((q) => q.id === over.id);

    setSelectedQuestions((items) => arrayMove(items, oldIndex, newIndex));
  }}
>
  <SortableContext
    items={selectedQuestions.map((q) => q.id)}
    strategy={verticalListSortingStrategy}
  >
    <div className="selected-questions-grid">
    {selectedQuestions.map((q, index) => (
  <SortableItem
    key={q.id}
    q={q}
    index={index}  // 👈 pass index here
    handleRemove={handleRemoveQuestion}
  />
))}
    </div>
  </SortableContext>
</DndContext>
)}



  
  




          <button className="save-btn" onClick={handleSaveAssignment}>💾 Save Assignment</button>
        </div>
      </div>
    </TutorLayout>
  );
}

export default AssignmentBuilder;